// Tiny signal-based toast system. Import showToast() anywhere; ToastHost (mounted
// in Layout) subscribes and renders. Toasts auto-dismiss after a kind-specific
// timeout (errors stay longer) and can be closed manually.

import { signal, type Signal } from "@preact/signals";

export type ToastKind = "success" | "error" | "info";

export interface Toast {
  id: number;
  kind: ToastKind;
  message: string;
  /** Auto-dismiss after this many ms. 0 disables auto-dismiss. */
  durationMs: number;
}

const MAX_VISIBLE = 5;
const DEFAULT_DURATION: Record<ToastKind, number> = {
  success: 3500,
  info: 4000,
  error: 6500,
};

export const toastsSignal: Signal<Toast[]> = signal([]);

let nextId = 1;

export interface ShowToastInput {
  kind?: ToastKind;
  message: string;
  durationMs?: number;
}

export function showToast(input: ShowToastInput): number {
  const kind = input.kind ?? "info";
  const id = nextId++;
  const toast: Toast = {
    id,
    kind,
    message: input.message,
    durationMs: input.durationMs ?? DEFAULT_DURATION[kind],
  };

  const next = [...toastsSignal.value, toast];
  // Drop oldest if we exceed the cap so the UI doesn't pile up.
  toastsSignal.value = next.length > MAX_VISIBLE ? next.slice(-MAX_VISIBLE) : next;

  if (toast.durationMs > 0 && typeof window !== "undefined") {
    window.setTimeout(() => dismissToast(id), toast.durationMs);
  }
  return id;
}

export function dismissToast(id: number): void {
  toastsSignal.value = toastsSignal.value.filter((t) => t.id !== id);
}

export function clearToasts(): void {
  toastsSignal.value = [];
}

// Convenience shortcuts.
export const toastSuccess = (message: string, durationMs?: number): number =>
  showToast({ kind: "success", message, durationMs });
export const toastError = (message: string, durationMs?: number): number =>
  showToast({ kind: "error", message, durationMs });
export const toastInfo = (message: string, durationMs?: number): number =>
  showToast({ kind: "info", message, durationMs });
